/* eslint-disable func-names */
import Intl from 'intl';
import 'intl/locale-data/jsonp/en-US';
import { schemaBreadcrumbLDJSON, schemaBreadcrumbLDJSONScript } from './schema';

/* eslint-disable no-multi-assign */
const animateScrollLeft = (pElement, finalLeft) => {
  const element = pElement;
  let id;
  let left = element.scrollLeft;
  const direction = finalLeft > left ? 'right' : 'left';
  const scrollLeft = () => {
    if (direction === 'left') {
      left -= 15;
    } else {
      left += 15;
    }
    element.scrollLeft = left;
    if (direction === 'left') {
      if (left <= finalLeft) {
        clearInterval(id);
      }
    } else if (left >= finalLeft) {
      clearInterval(id);
    }
  };
  id = setInterval(scrollLeft, 10);
};

const baseURL = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '';

const breakpoint = () =>
  typeof window === 'undefined'
    ? 'small'
    : window.getComputedStyle(document.querySelector('body'), ':before').getPropertyValue('content').replace(/"/g, '');

const camelCaseToDash = (str) => str.replace(/([a-zA-Z])(?=[A-Z])/g, '$1-').toLowerCase();

const capitalize = (str) =>
  String(str)
    .toLowerCase()
    .replace(/(^| )(\w)/g, (x) => x.toUpperCase());

const debounce = (callback, wait, immediate = false) => {
  let timeout = null;
  return () => {
    const callNow = immediate && !timeout;
    // eslint-disable-next-line no-undef
    const next = () => callback.apply(this, arguments);
    clearTimeout(timeout);
    timeout = setTimeout(next, wait);
    if (callNow) {
      next();
    }
  };
};

const getDevice = () => {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent.toLowerCase();
  if (
    /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
      ua
    )
  ) {
    return 'tablet';
  } else if (
    /(mobi|ipod|phone|blackberry|opera mini|fennec|minimo|symbian|psp|nintendo ds|archos|skyfire|puffin|blazer|bolt|gobrowser|iris|maemo|semc|teashark|uzard)/.test(
      ua
    )
  ) {
    return 'phone';
  }
  return 'desktop';
};

const getDistanceFromTop = (e, { top: t } = e.getBoundingClientRect()) => t - innerHeight;

const getQueryParams = (stringToParse = window.location.search.lastIndexOf('?') + 1) => {
  const search = stringToParse.substring(stringToParse.lastIndexOf('?') + 1);
  return JSON.parse(`{"${search.replace(/&/g, '","').replace(/=/g, '":"')}"}`, (key, value) =>
    key === '' ? value : decodeURIComponent(value)
  );
};

const dollars = (price) => {
  if (price === undefined) return '';
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

const round = (value, decimals) => Number(`${Math.round(`${value}e${decimals}`)}e-${decimals}`);

const getOrientation = () => {
  if (typeof window === 'undefined') {
    return '';
  } else if (window.matchMedia('(orientation: landscape)').matches) {
    return 'landscape';
  }
  return 'portrait';
};

const getParents = (el, parentParam) => {
  let parentSelector = parentParam;
  if (parentSelector === undefined) {
    parentSelector = document;
  }
  const parents = [];
  let p = el.parentNode;
  while (p !== parentSelector) {
    const o = p;
    parents.push(o);
    p = o.parentNode;
  }
  parents.push(parentSelector);
  return parents;
};

const getScript = (source, callback, container = document.getElementsByTagName('head')[0]) =>
  new Promise((resolve) => {
    const scripts = document.querySelectorAll(`script[src="${source}"]`);
    if (scripts.length < 1) {
      let script = document.createElement('script');
      script.async = 1;
      script.onload = script.onreadystatechange = (_, isAbort) => {
        if (isAbort || !script.readyState || /loaded|complete/.test(script.readyState)) {
          script.onload = script.onreadystatechange = null;
          script.setAttribute('data-loadstate', 'loaded');
          script = undefined;
          if (!isAbort) {
            if (callback) {
              callback();
            }
            resolve();
          }
        }
      };
      script.src = source;
      container.appendChild(script);
    } else {
      const node = scripts[0];
      const isLoaded = node.getAttribute('data-loadstate');
      if (isLoaded && isLoaded === 'loaded') {
        if (callback) {
          callback();
        }
        resolve();
      } else {
        const oldOnLoad = node.onload;
        node.onload = (_, isAbort) => {
          oldOnLoad(_, isAbort);
          if (!isAbort) {
            if (callback) {
              callback();
            }
            resolve();
          }
        };
      }
    }
  });

const isIE11 = typeof window === 'undefined' ? false : !!window.MSInputMethodContext && !!document.documentMode;

const isInViewport = (e, { top: t, height: h } = e.getBoundingClientRect()) => t <= innerHeight && t + h >= 0;

const matches = (el, selector) =>
  (
    el.matches ||
    el.matchesSelector ||
    el.msMatchesSelector ||
    el.mozMatchesSelector ||
    el.webkitMatchesSelector ||
    el.oMatchesSelector
  ).call(el, selector);

const smoothScroll = (node, topOrLeft = 'top', horizontal = false) => {
  const testSupportsSmoothScroll = () => {
    let supports = false;
    try {
      const div = document.createElement('div');
      div.scrollTo({
        top: 0,
        get behavior() {
          supports = true;
          return 'smooth';
        },
      });
      // eslint-disable-next-line no-empty
    } catch (err) {}
    return supports;
  };
  const easingOutQuint = (x, t, b, c, d) => {
    // via https://github.com/tootsuite/mastodon/blob/f59ed3a4fafab776b4eeb92f805dfe1fecc17ee3/app/javascript/mastodon/scroll.js
    let $t = t;
    return c * (($t = $t / d - 1) * $t * $t * $t * $t + 1) + b;
  };
  const hasNativeSmoothScroll = testSupportsSmoothScroll();
  const smoothScrollPolyfill = (nodeParam, key, target) => {
    const $node = nodeParam;
    const startTime = Date.now();
    const offset = node[key];
    const gap = target - offset;
    const duration = 1000;
    let interrupt = false;

    const cancel = () => {
      interrupt = true;
      // eslint-disable-next-line no-use-before-define
      cleanup();
    };

    const cleanup = () => {
      $node.removeEventListener('wheel', cancel);
      $node.removeEventListener('touchstart', cancel);
    };

    const step = () => {
      const elapsed = Date.now() - startTime;
      const percentage = elapsed / duration;
      if (interrupt) {
        return;
      }
      if (percentage > 1) {
        cleanup();
        return;
      }
      $node[key] = easingOutQuint(0, elapsed, offset, gap, duration);
      requestAnimationFrame(step);
    };
    $node.addEventListener('wheel', cancel, { passive: true });
    $node.addEventListener('touchstart', cancel, { passive: true });
    step();
    return cancel;
  };
  if (hasNativeSmoothScroll) {
    return node.scrollTo({
      [horizontal ? 'left' : 'top']: topOrLeft,
      behavior: 'smooth',
    });
  }
  return smoothScrollPolyfill(node, horizontal ? 'scrollLeft' : 'scrollTop', topOrLeft);
};

const scrollToSection = (hash, yOffset = 0) => {
  const to = document.querySelector(hash);
  if (to) {
    // to.scrollIntoView({ behavior: 'smooth' });
    const yCoordinate = to.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({
      top: yCoordinate + yOffset,
      behavior: 'smooth',
    });
  }
};

const scrollTillYouReachElement = (elmnt, timeToCheck, numberOfTries) => {
  const bounding = elmnt.getBoundingClientRect();
  let numTries = numberOfTries || 0;
  if (!(bounding.top >= 0 && bounding.top <= 1)) {
    elmnt.scrollIntoView();
    numTries += 1;
    // Check recursively and scroll till the element appears in the view port. Check three times and quit.
    if (numTries <= 3) {
      setTimeout(
        (element, time, numberTries) => {
          scrollTillYouReachElement(element, time, numberTries);
        },
        timeToCheck || 800,
        elmnt,
        timeToCheck,
        numTries
      );
    }
  }
};

const throttle = (func, delay = 500) => {
  let timeout = null;
  return function (...args) {
    if (!timeout) {
      timeout = setTimeout(() => {
        func.call(this, ...args);
        timeout = null;
      }, delay);
    }
  };
};

export {
  animateScrollLeft,
  baseURL,
  breakpoint,
  camelCaseToDash,
  capitalize,
  debounce,
  dollars,
  getDevice,
  getDistanceFromTop,
  getOrientation,
  getQueryParams,
  getParents,
  getScript,
  isIE11,
  isInViewport,
  matches,
  round,
  schemaBreadcrumbLDJSON,
  schemaBreadcrumbLDJSONScript,
  scrollToSection,
  smoothScroll,
  scrollTillYouReachElement,
  throttle,
};
